import { Component, OnInit } from '@angular/core';
import { AuthDialogMode } from '@models/generic/auth-dialog-mode.enum';
import { SignInDto } from '@models/users/sign-in.dto';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ListboxChangeEvent } from 'primeng/listbox';

@Component({
  selector: 'ebs-authorized-users-dialog',
  templateUrl: './authorized-users-dialog.component.html',
  styleUrls: ['./authorized-users-dialog.component.scss'],
})
export class AuthorizedUsersDialogComponent implements OnInit {
  public selectedUsers: SignInDto[] = [];
  public allUsers: SignInDto[] = [];

  public AuthDialogMode: typeof AuthDialogMode = AuthDialogMode;
  public mode = AuthDialogMode.AUTH_LIST;

  public search: string = '';

  public simplifiedView: boolean = true;
  public allowSimplifiedView: boolean = true;

  public sectorsOptions: string[] = [];
  public headquartersOptions: string[] = [];
  public rolesOptions: string[] = [];
  public externalsOptions: boolean[] = [];
  public groupsOptions: string[] = [];
  public typeContractOptions: string[] = [];

  public selectedSectorsOptions: string[] = [];
  public selectedHeadquartersOptions: string[] = [];
  public selectedRolesOptions: string[] = [];
  public selectedExternalsOptions: boolean[] = [];
  public selectedGroupsOptions: string[] = [];
  public selectedTypeContractOptions: string[] = [];

  public sectors: Filter<string>[] = [];
  public headquarters: Filter<string>[] = [];
  public roles: Filter<string>[] = [];
  public groups: Filter<string>[] = [];
  public externals: Filter<boolean>[] = [];
  public typeContract: Filter<string>[] = [];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
  ) {}

  ngOnInit() {
    this.setFiltersToDefault();
    this.allowSimplifiedView = !!this.config.data.allowSimplifiedView;
    this.simplifiedView = this.allowSimplifiedView;
  }

  cancel() {
    this.ref.close(false);
  }
  confirm() {
    this.ref.close(this.selectedUsers);
  }

  externalFilterChanged(event: ListboxChangeEvent) {
    this.selectedExternalsOptions = event.value.map((elem) => elem === 'true');
    this.evaluateFilters();
  }

  roleFilterChanged(event: ListboxChangeEvent) {
    this.selectedRolesOptions = event.value;
    this.evaluateFilters();
  }

  headquarterFilterChanged(event: ListboxChangeEvent) {
    this.selectedHeadquartersOptions = event.value;
    this.evaluateFilters();
  }

  sectorFilterChanged(event: ListboxChangeEvent) {
    this.selectedSectorsOptions = event.value;
    this.evaluateFilters();
  }

  groupFilterChanged(event: ListboxChangeEvent) {
    this.selectedGroupsOptions = event.value;
    this.evaluateFilters();
  }

  typeContractFilterChanged(event: ListboxChangeEvent) {
    this.selectedTypeContractOptions = event.value;
    this.evaluateFilters();
  }

  evaluateFilters() {
    this.selectedUsers = this.allUsers
      .filter((user) => {
        return (
          this.selectedExternalsOptions.length === 0 ||
          this.selectedExternalsOptions.includes(user.subscriber!.isExternal)
        );
      })
      .filter((user) => {
        return (
          this.selectedHeadquartersOptions.length === 0 ||
          this.selectedHeadquartersOptions.includes(
            user.subscriber!.headquarter,
          )
        );
      })
      .filter((user) => {
        return (
          this.selectedRolesOptions.length === 0 ||
          this.selectedRolesOptions.includes(user.subscriber!.role)
        );
      })
      .filter((user) => {
        return (
          this.selectedSectorsOptions.length === 0 ||
          this.selectedSectorsOptions.includes(user.subscriber!.sector)
        );
      })
      .filter((user) => {
        return (
          this.selectedGroupsOptions.length === 0 ||
          this.selectedGroupsOptions.includes(user.group!.description)
        );
      }).filter((user) => {
        return (
          this.selectedTypeContractOptions.length === 0 ||
          this.selectedTypeContractOptions.includes(user.subscriber!.typeContract)
        );
      });
    this.sectors = this.calculateFilterArray(
      this.selectedUsers.map((auth) => auth.subscriber!.sector),
      this.sectorsOptions,
    );
    this.headquarters = this.calculateFilterArray(
      this.selectedUsers.map((auth) => auth.subscriber!.headquarter),
      this.headquartersOptions,
    );
    this.roles = this.calculateFilterArray(
      this.selectedUsers.map((auth) => auth.subscriber!.role),
      this.rolesOptions,
    );
    this.externals = this.calculateFilterArray(
      this.selectedUsers.map((auth) => auth.subscriber!.isExternal),
      this.externalsOptions,
    );
    this.groups = this.calculateFilterArray(
      this.selectedUsers.map((auth) => auth.group!.description),
      this.groupsOptions,
    );
    this.typeContract = this.calculateFilterArray(
      this.selectedUsers.map((auth) => auth.subscriber!.typeContract),
      this.typeContractOptions,
    );
  }

  setFiltersToDefault() {
    this.selectedUsers = this.config.data.selectedUsers;
    this.allUsers = this.config.data.allUsers;
    this.sectorsOptions = Array.from(
      new Set(this.config.data.allUsers.map((auth) => auth.subscriber.sector)),
    );
    this.headquartersOptions = Array.from(
      new Set(
        this.config.data.allUsers.map((auth) => auth.subscriber.headquarter),
      ),
    );
    this.rolesOptions = Array.from(
      new Set(this.config.data.allUsers.map((auth) => auth.subscriber.role)),
    );
    this.externalsOptions = Array.from(
      new Set(
        this.config.data.allUsers.map((auth) => auth.subscriber.isExternal),
      ),
    );
    this.groupsOptions = Array.from(
      new Set(this.config.data.allUsers.map((auth) => auth.group!.description)),
    );
    this.typeContractOptions = Array.from(
      new Set(this.config.data.allUsers.map((auth) => auth.subscriber!.typeContract)),
    );
    this.sectors = this.calculateFilterArray(
      this.allUsers.map((auth) => auth.subscriber!.sector),
      this.sectorsOptions,
    );
    this.headquarters = this.calculateFilterArray(
      this.allUsers.map((auth) => auth.subscriber!.headquarter),
      this.headquartersOptions,
    );
    this.roles = this.calculateFilterArray(
      this.allUsers.map((auth) => auth.subscriber!.role),
      this.rolesOptions,
    );
    this.externals = this.calculateFilterArray(
      this.allUsers.map((auth) => auth.subscriber!.isExternal),
      this.externalsOptions,
    );
    this.groups = this.calculateFilterArray(
      this.allUsers.map((auth) => auth.group!.description),
      this.groupsOptions,
    );
    this.typeContract = this.calculateFilterArray(
      this.allUsers.map((auth) => auth.subscriber!.typeContract),
      this.typeContractOptions,
    );
  }

  calculateFilterArray(arr: any[], options: any[] = []): Filter<any>[] {
    let map = {};
    options.forEach((elem) => {
      map[elem] = 0;
    });

    arr.forEach((elem) => {
      if (map[elem] === undefined) {
        map[elem] = 0;
      }
      map[elem] = map[elem] + 1;
    });

    return Object.keys(map).map((elem) => {
      return {
        value: elem,
        count: map[elem],
      };
    });
  }
}

export interface Filter<T> {
  value: T;
  count: number;
}
